import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Slider,
} from '@mui/material';
import { FilterPanel } from './LMA.style';
import { FilterValues } from './ActiveFilters';

interface FilterPanelComponentProps {
  filterOpen: boolean;
  filters: FilterValues;
  onFilterChange: (field: string, value: string) => void;
  onClearFilters: () => void;
  onLeadTypeChange?: (leadTypes: string[]) => void;
  selectedLeadTypes?: string[];
  hideLeadTypeFilter?: boolean;
  hideDedupeStatusFilter?: boolean;
  // filteredDataCount: number;
}

const FilterPanelComponent: React.FC<FilterPanelComponentProps> = ({
  filterOpen,
  filters,
  onFilterChange,
  onClearFilters,
  onLeadTypeChange,
  selectedLeadTypes = [],
  hideLeadTypeFilter = false,
  hideDedupeStatusFilter = false,
  // filteredDataCount,
}) => {
  const [dateRangeType, setDateRangeType] = useState<string>('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [matchPercentageRange, setMatchPercentageRange] = useState<number[]>([
    parseInt(filters.matchPercentageMin) || 0,
    parseInt(filters.matchPercentageMax) || 100,
  ]);
  const [tempFilters, setTempFilters] = useState<FilterValues>(filters);
  const [tempLeadTypes, setTempLeadTypes] = useState<string[]>(selectedLeadTypes);

  const activeFilterCount =
    Object.values(filters).filter((v) => v !== '').length +
    selectedLeadTypes.length +
    (dateRangeType ? 1 : 0);

  // Sync match percentage range with filter values
  useEffect(() => {
    const min = parseInt(filters.matchPercentageMin) || 0;
    const max = parseInt(filters.matchPercentageMax) || 100;
    setMatchPercentageRange([min, max]);
  }, [filters.matchPercentageMin, filters.matchPercentageMax]);

  const handleLeadTypeToggle = (leadType: string, event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newLeadTypes = tempLeadTypes.includes(leadType)
      ? tempLeadTypes.filter((type) => type !== leadType)
      : [...tempLeadTypes, leadType];

    setTempLeadTypes(newLeadTypes);
  };

  const handleDateRangeChange = (value: string) => {
    setDateRangeType(value);
    setShowCustomDateRange(value === 'custom');

    if (value !== 'custom') {
      // Calculate date range based on selection
      const today = new Date();
      let startDate = '';
      let endDate = today.toISOString().split('T')[0];

      switch (value) {
        case 'today':
          startDate = endDate;
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          startDate = yesterday.toISOString().split('T')[0];
          endDate = startDate;
          break;
        case 'thisWeek':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          startDate = weekStart.toISOString().split('T')[0];
          break;
        case 'lastWeek':
          const lastWeekEnd = new Date(today);
          lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
          const lastWeekStart = new Date(lastWeekEnd);
          lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
          startDate = lastWeekStart.toISOString().split('T')[0];
          endDate = lastWeekEnd.toISOString().split('T')[0];
          break;
        case 'last30Days':
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          startDate = thirtyDaysAgo.toISOString().split('T')[0];
          break;
        default:
          startDate = '';
          endDate = '';
      }

      if (startDate) {
        handleTempFilterChange('dateFrom', startDate);
      }
      if (endDate) {
        handleTempFilterChange('dateTo', endDate);
      }
    }
  };

  const handleClearAll = () => {
    onClearFilters();
    setDateRangeType('');
    setShowCustomDateRange(false);
    setTempFilters({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      dob: '',
      gender: '',
      address: '',
      autoMerge: '',
      manualReview: '',
      newRecord: '',
      qualifiedProtocol: '',
      associatedProtocol: '',
      protocol: '',
      site: '',
      dateFrom: '',
      dateTo: '',
      leadType: '',
      matchPercentageMin: '',
      matchPercentageMax: '',
      status: '',
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      sID: '',
      psecID: '',
      gclid: '',
      fbclid: '',
      atid: '',
    });
    setTempLeadTypes([]);
    if (onLeadTypeChange) {
      onLeadTypeChange([]);
    }
  };

  const handleApplyFilters = () => {
    // Apply all temp filters to actual filters
    Object.keys(tempFilters).forEach((key) => {
      onFilterChange(key, tempFilters[key]);
    });

    // Apply lead type filters
    if (onLeadTypeChange) {
      onLeadTypeChange(tempLeadTypes);
    }
  };

  const handleTempFilterChange = (field: keyof FilterValues, value: string) => {
    setTempFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const leadTypes = [
    { value: 'hot', label: 'Hot Lead', color: '#FF4444', bgColor: '#FFE5E5' },
    { value: 'warm', label: 'Warm Lead', color: '#FFA500', bgColor: '#FFF4E5' },
    { value: 'cold', label: 'Cold Lead', color: '#4A90E2', bgColor: '#E5F2FF' },
  ];

  return (
    <Collapse in={filterOpen} timeout="auto" unmountOnExit>
      <FilterPanel>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px', paddingLeft: '4px' }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={handleClearAll}
              disabled={activeFilterCount === 0}
              sx={{
                fontSize: `12px`,
                textTransform: 'none',
                color: (theme: any) => theme.palette.error?.main,
              }}
            >
              Clear All
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilters}
              sx={{
                fontSize: `12px`,
                padding: '4px 8px',
                textTransform: 'none',
                backgroundColor: (theme: any) => theme.palette.themeColor?.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: (theme: any) =>
                    theme.palette.themeColor?.dark || theme.palette.themeColor?.main,
                },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>

        {/* Main Layout: Filter Cards and Checkboxes Side by Side */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', lg: 'row' },
            mt: 1,
          }}
        >
          {/* Left Side: Filter Cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
            {/* Base Filters Card */}
            <Box
              sx={{
                border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
                borderRadius: 2,
                p: 1.5,
                backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  size="small"
                  label="First Name"
                  value={tempFilters.firstName || ''}
                  onChange={(e) => handleTempFilterChange('firstName', e.target.value)}
                />
                <TextField
                  size="small"
                  label="Last Name"
                  value={tempFilters.lastName || ''}
                  onChange={(e) => handleTempFilterChange('lastName', e.target.value)}
                />
                <TextField
                  size="small"
                  label="Email"
                  value={tempFilters.email || ''}
                  onChange={(e) => handleTempFilterChange('email', e.target.value)}
                />
                <TextField
                  size="small"
                  label="Phone"
                  value={tempFilters.phone || ''}
                  onChange={(e) => handleTempFilterChange('phone', e.target.value)}
                />
                <FormControl size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={tempFilters.gender || ''}
                    label="Gender"
                    onChange={(e) => handleTempFilterChange('gender', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Date of Birth"
                  type="date"
                  value={tempFilters.dob || ''}
                  onChange={(e) => handleTempFilterChange('dob', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="ZIP"
                  value={tempFilters.address || ''}
                  onChange={(e) => handleTempFilterChange('address', e.target.value)}
                />
              </Box>
            </Box>

            {/* Additional Parameters Card */}
            <Box
              sx={{
                border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
                borderRadius: 2,
                p: 1.5,
                backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  },
                  gap: 1.5,
                }}
              >
                <FormControl size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRangeType}
                    label="Date Range"
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                  >
                    <MenuItem value="">All Dates</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="yesterday">Yesterday</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="last30Days">Last 30 Days</MenuItem>
                    <MenuItem value="custom">Custom Date Range</MenuItem>
                  </Select>
                </FormControl>

                {showCustomDateRange && (
                  <>
                    <TextField
                      size="small"
                      label="From Date"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />

                    <TextField
                      size="small"
                      label="To Date"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => onFilterChange('dateTo', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </>
                )}

                <FormControl size="small">
                  <InputLabel>Qualified Protocol</InputLabel>
                  <Select
                    value={tempFilters.qualifiedProtocol || ''}
                    label="Qualified Protocol"
                    onChange={(e) => handleTempFilterChange('qualifiedProtocol', e.target.value)}
                  >
                    <MenuItem value="">All Protocols</MenuItem>
                    <MenuItem value="ABC">Protocol ABC</MenuItem>
                    <MenuItem value="XYZ">Protocol XYZ</MenuItem>
                    <MenuItem value="DEF">Protocol DEF</MenuItem>
                    <MenuItem value="EFG">Protocol EFG</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <InputLabel>Site</InputLabel>
                  <Select
                    value={tempFilters.site}
                    label="Site"
                    onChange={(e) => handleTempFilterChange('site', e.target.value)}
                  >
                    <MenuItem value="">All Sites</MenuItem>
                    <MenuItem value="site1">Site 1 - New York</MenuItem>
                    <MenuItem value="site2">Site 2 - Los Angeles</MenuItem>
                    <MenuItem value="site3">Site 3 - Chicago</MenuItem>
                    <MenuItem value="site4">Site 4 - Houston</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ ml: 1 }}>
                  <span
                    style={{
                      marginBottom: '-8px',
                      marginTop: '-2px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#555',
                    }}
                  >
                    Match %: {matchPercentageRange[0]}-{matchPercentageRange[1]}%
                  </span>
                  <Slider
                    value={matchPercentageRange}
                    onChange={(_event: Event, newValue: number | number[]) => {
                      const range = newValue as number[];
                      setMatchPercentageRange(range);
                      handleTempFilterChange('matchPercentageMin', range[0].toString());
                      handleTempFilterChange('matchPercentageMax', range[1].toString());
                    }}
                    valueLabelDisplay="auto"
                    min={0}
                    max={100}
                    size="small"
                  />
                </FormControl>
              </Box>
            </Box>
          </Box>

          {/* Right Side: Checkboxes Card */}
          {!hideLeadTypeFilter && !hideDedupeStatusFilter && (
            <Box
              sx={{
                border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
                borderRadius: 2,
                p: 2,
                backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
                minWidth: { lg: '300px' },
                maxWidth: { lg: '350px' },
              }}
            >
              {/* Checkboxes in two columns */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 0,
                }}
              >
                {/* Lead Type Checkboxes */}
                {!hideLeadTypeFilter && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 0.5,
                        color: (theme: any) => theme.palette.neutral8?.main,
                      }}
                    >
                      Lead Type
                    </Typography>
                    <FormGroup>
                      {leadTypes.map((leadType) => (
                        <FormControlLabel
                          key={leadType.value}
                          control={
                            <Checkbox
                              checked={tempLeadTypes.includes(leadType.value)}
                              onChange={(e) => handleLeadTypeToggle(leadType.value, e)}
                              disableRipple
                              sx={{
                                color: leadType.color,
                                '&.Mui-checked': {
                                  color: leadType.color,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: '14px' }}>{leadType.label}</Typography>
                          }
                          sx={{
                            backgroundColor: tempLeadTypes.includes(leadType.value)
                              ? leadType.bgColor
                              : 'transparent',
                            borderRadius: 1,
                            p: 0,
                            mr: 0,
                            ml: 0,
                            transition: 'all 0.2s',
                            border: `1px solid ${
                              tempLeadTypes.includes(leadType.value)
                                ? leadType.color
                                : 'transparent'
                            }`,
                            '& .MuiCheckbox-root': {
                              padding: '8px',
                            },
                          }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}

                {/* Dedupe Status Checkboxes */}
                {!hideDedupeStatusFilter && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        mb: 0.5,
                        color: (theme: any) => theme.palette.neutral8?.main,
                      }}
                    >
                      Dedupe Status
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={tempFilters.status?.includes('new') || false}
                            onChange={(e) => {
                              const currentStatuses =
                                tempFilters.status?.split(',').filter((s) => s) || [];
                              const newStatuses = e.target.checked
                                ? [...currentStatuses, 'new']
                                : currentStatuses.filter((s) => s !== 'new');
                              handleTempFilterChange('status', newStatuses.join(','));
                            }}
                            disableRipple
                            sx={{
                              color: (theme: any) => theme.palette.neutral5?.main,
                              '&.Mui-checked': {
                                color: (theme: any) => theme.palette.themeColor?.main,
                              },
                            }}
                          />
                        }
                        label={<Typography sx={{ fontSize: '14px' }}>New</Typography>}
                        sx={{
                          borderRadius: 1,
                          p: 0,
                          mr: 0,
                          ml: 0,
                          '& .MuiCheckbox-root': {
                            padding: '8px',
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={tempFilters.status?.includes('auto-merge') || false}
                            onChange={(e) => {
                              const currentStatuses =
                                tempFilters.status?.split(',').filter((s) => s) || [];
                              const newStatuses = e.target.checked
                                ? [...currentStatuses, 'auto-merge']
                                : currentStatuses.filter((s) => s !== 'auto-merge');
                              handleTempFilterChange('status', newStatuses.join(','));
                            }}
                            disableRipple
                            sx={{
                              color: (theme: any) => theme.palette.neutral5?.main,
                              '&.Mui-checked': {
                                color: (theme: any) => theme.palette.themeColor?.main,
                              },
                            }}
                          />
                        }
                        label={<Typography sx={{ fontSize: '14px' }}>Auto Merge</Typography>}
                        sx={{
                          borderRadius: 1,
                          p: 0,
                          mr: 0,
                          ml: 0,
                          '& .MuiCheckbox-root': {
                            padding: '8px',
                          },
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={tempFilters.status?.includes('manual') || false}
                            onChange={(e) => {
                              const currentStatuses =
                                tempFilters.status?.split(',').filter((s) => s) || [];
                              const newStatuses = e.target.checked
                                ? [...currentStatuses, 'manual']
                                : currentStatuses.filter((s) => s !== 'manual');
                              handleTempFilterChange('status', newStatuses.join(','));
                            }}
                            disableRipple
                            sx={{
                              color: (theme: any) => theme.palette.neutral5?.main,
                              '&.Mui-checked': {
                                color: (theme: any) => theme.palette.themeColor?.main,
                              },
                            }}
                          />
                        }
                        label={<Typography sx={{ fontSize: '14px' }}>Manual Review</Typography>}
                        sx={{
                          borderRadius: 1,
                          p: 0,
                          mr: 0,
                          ml: 0,
                          '& .MuiCheckbox-root': {
                            padding: '8px',
                          },
                        }}
                      />
                    </FormGroup>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
        {/* UTM Tracking Parameters Card */}
        <Box
          sx={{
            border: (theme: any) => `1px solid ${theme.palette.neutral3?.main || '#E9E9E9'}`,
            borderRadius: 2,
            p: 1.5,
            mt: 1,
            backgroundColor: (theme: any) => theme.palette.background?.paper || '#fff',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 1.5,
            }}
          >
            <FormControl size="small">
              <InputLabel>UTM Source</InputLabel>
              <Select
                value={tempFilters.utm_source || ''}
                label="UTM Source"
                onChange={(e) => handleTempFilterChange('utm_source', e.target.value)}
              >
                <MenuItem value="">All Sources</MenuItem>
                <MenuItem value="Facebook">Facebook</MenuItem>
                <MenuItem value="Instagram">Instagram</MenuItem>
                <MenuItem value="Google">Google</MenuItem>
                <MenuItem value="Twitter">Twitter</MenuItem>
                <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                <MenuItem value="Email">Email</MenuItem>
                <MenuItem value="Direct">Direct</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>UTM Medium</InputLabel>
              <Select
                value={tempFilters.utm_medium || ''}
                label="UTM Medium"
                onChange={(e) => handleTempFilterChange('utm_medium', e.target.value)}
              >
                <MenuItem value="">All Mediums</MenuItem>
                <MenuItem value="CPC">CPC</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="social">Social</MenuItem>
                <MenuItem value="organic">Organic</MenuItem>
                <MenuItem value="referral">Referral</MenuItem>
                <MenuItem value="display">Display</MenuItem>
                <MenuItem value="affiliate">Affiliate</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="UTM Campaign"
              value={tempFilters.utm_campaign || ''}
              onChange={(e) => handleTempFilterChange('utm_campaign', e.target.value)}
              placeholder="e.g., Winter2025Campaign"
            />

            <TextField
              size="small"
              label="UTM Term"
              value={tempFilters.utm_term || ''}
              onChange={(e) => handleTempFilterChange('utm_term', e.target.value)}
              placeholder="e.g., diabetes treatment"
            />

            <TextField
              size="small"
              label="UTM Content"
              value={tempFilters.utm_content || ''}
              onChange={(e) => handleTempFilterChange('utm_content', e.target.value)}
              placeholder="e.g., ad_variation_A"
            />
          </Box>
        </Box>
      </FilterPanel>
    </Collapse>
  );
};

export default FilterPanelComponent;
